import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  Header,
  Checkbox,
  Flashbar,
  Spinner
} from "@amzn/awsui-components-react";
import { capitalizeFirstLetter, downloadJsonData } from "src/Ateam-BI/utils/generalUtils";
import { DownloadAppModalProps, getProcessResponse, ProcessData, ProcessesData } from "./Types/downloadAppModal";
import { PROCESS_TYPE_CONFIG } from "./Constants/downloadAppModal";
import { TEMPLATE_APP } from "../Constants";

const DownloadAppModal = ({ 
  processes,
  appData, 
  hideModal
}: DownloadAppModalProps) => {
  const [processesToInclude, setProcessesToInclude] = useState<Set<string>>(new Set());
  const [checkAll, setCheckAll] = useState(true);
  const [loadingFullProcessInfoMap, setLoadingFullProcessInfoMap] = useState(false)
  const [fullProcessInfoMap, setFullProcessInfoMap] = useState<{[key: string]: ProcessData}>({})

  const getProcessSetId = (processType:string, process:ProcessData) => {
    const {processKey} = PROCESS_TYPE_CONFIG[processType];
    return `${processType}#${process[processKey]}`;
  }

  const createProcessesSet = (allProcesses: ProcessesData) => {
    return Object.keys(allProcesses).reduce((processSet:Set<string>, processType:string) => {
      allProcesses[processType].forEach((process:ProcessData) => processSet.add(getProcessSetId(processType, process)))
      return processSet;
    }, new Set<string>())
  }

  const loadFullProcessInfoMap = async (allProceses: ProcessesData) => {
    setLoadingFullProcessInfoMap(true)
    const processPromiseList = Object.keys(allProceses).reduce((promiseList:Promise<any>[], processType) => {
      const {getProcess, processKey, getProcessResponse} = PROCESS_TYPE_CONFIG[processType];
      allProceses[processType].map(process => {
        const id = process[processKey]
        if (id) {
          promiseList.push(new Promise((resolve) => {
            getProcess.apply(this, [id, (response:getProcessResponse) => {
              const r = response.data[getProcessResponse];
              const processSetId = getProcessSetId(processType, process);
              fullProcessInfoMap[processSetId] = r;
              setFullProcessInfoMap(fullProcessInfoMap)
              resolve(r)
        }])}))
      } else {
          promiseList.push(new Promise((resolve) => resolve({})))
        }
      })
      return promiseList;
    }, [])
    await Promise.all(processPromiseList)
    setLoadingFullProcessInfoMap(false)
  }

  useEffect(() => {
    loadFullProcessInfoMap(processes);
  }, [processes])

  useEffect(() => {
    checkAll ? 
      setProcessesToInclude(new Set(createProcessesSet(processes)))
      : setProcessesToInclude(new Set());
  }, [checkAll, processes])

  const getProcessesToDownload = (allProcesses:ProcessesData) => {
    let processesToDownload: {[key:string]:ProcessData[]} = {};
    Object.keys(allProcesses).forEach((processType:string) => {

      const processToDownloadOfType = allProcesses[processType].filter(process => {
        const processSetId = getProcessSetId(processType, process);
        return processesToInclude.has(processSetId);
      })
      
      processesToDownload[processType] = processToDownloadOfType.map((process) => {
        const {processKey} = PROCESS_TYPE_CONFIG[processType];
        const processSetId = getProcessSetId(processType, process);
        const fullProcessInfo = structuredClone(fullProcessInfoMap[processSetId]);
        delete fullProcessInfo[processKey];
        return fullProcessInfo;
      })

    })
    return processesToDownload;
  }

  const downloadData = (allProcesses:ProcessesData) => {
    const filename = appData.app_name;
    const downloadObject = structuredClone(appData);
    delete downloadObject[TEMPLATE_APP.APP_ID]
    downloadObject.processes = getProcessesToDownload(structuredClone(allProcesses));
    downloadJsonData(filename, downloadObject);
  }

  return (
    <Modal
      onDismiss={hideModal}
      visible={true}
      header={`Download App`}
      footer={
        <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={hideModal}>Cancel</Button>
                <Button variant="primary" disabled={loadingFullProcessInfoMap} onClick={() => {
                    downloadData(processes)
                }}>
                  {loadingFullProcessInfoMap && (<Spinner/>)} Download
                </Button>
            </SpaceBetween>
        </Box>
      }
      size="medium"
    >
      <Box margin={{bottom:"m"}}>
        <Box margin={{bottom:"m"}}>
          <Flashbar
            items={[{
                type: "info",
                content: "Links for QuickSights Dashboards will need to be updated upon upload to prod",
                dismissible: false,
            }]} 
          />
        </Box>
        <Header variant="h2">Processes to Include:</Header>
        <Box margin={{left:"m", top:"xs", bottom:"s"}}>
          <Checkbox
            checked={checkAll}
            onChange={()=>{setCheckAll(!checkAll)}}
          > Select/Deselect All </Checkbox>
        </Box>

        <Box margin={{left:"m"}}>
          <SpaceBetween size="m">
            {Object.keys(processes).map((processType:string, index: number)  => {
              return (
                <Box key={index}>
                  <Header variant="h3">{capitalizeFirstLetter(processType)}</Header>
                  <Box margin={{left:"m"}}>
                    {processes[processType].length > 0 ? processes[processType].map((process:ProcessData, innerIndex: number) => {
                      const {processLabel} = PROCESS_TYPE_CONFIG[processType];
                      const processSetId = getProcessSetId(processType, process);
                      return (
                        <Checkbox 
                          key={innerIndex}
                          checked={processesToInclude.has(processSetId)}
                          onChange={() => {
                            processesToInclude.has(processSetId)
                              ? processesToInclude.delete(processSetId)
                              : processesToInclude.add(processSetId);
                            setProcessesToInclude(new Set(processesToInclude))
                          }}
                        >{process[processLabel]}</Checkbox>
                      )
                    }) : (
                      <Box color="text-status-inactive">None Available</Box>
                    )}
                  </Box>
                </Box>
              )
            })}
          </SpaceBetween>
        </Box>
      </Box>
    </Modal>
  );
};

export default DownloadAppModal;
